import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Spinner } from '@nextui-org/react';
import { ChevronDown, LogIn, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import commentService, {
  CommentEntityType,
  EntityComment,
  replyIndentPx,
} from '../../services/commentService';
import CommentComposer from './CommentComposer';
import CommentItem from './CommentItem';
import ConfirmDialog from '../ConfirmDialog';
import { t as tr } from '../../i18n';

const ACCENT = '#3fced1';
const PAGE_SIZE = 20;

interface CommentSectionProps {
  entityType: CommentEntityType;
  entityId: string;
  title?: string;
}

/**
 * Wiederverwendbare Kommentar-Sektion für Pro-Events und News-Artikel.
 * Features: verschachtelte Antworten, Nachladen von Antworten, Pagination,
 * optimistisches Einfügen, Löschen eigener Kommentare, Gäste-Login-Hinweis.
 */
const CommentSection: React.FC<CommentSectionProps> = ({
  entityType,
  entityId,
  title = tr('web.component.post-card.kommentare'),
}) => {
  const { user, isAuthenticated } = useAuth();

  const [comments, setComments] = useState<EntityComment[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Vollständig nachgeladene Antworten pro Top-Level-Kommentar
  const [extraReplies, setExtraReplies] = useState<Record<string, EntityComment[]>>({});
  const [loadingRepliesFor, setLoadingRepliesFor] = useState<string | null>(null);

  // Inline-Antworten-Composer: threadId = Top-Level-Kommentar, parent = geklickter Kommentar
  const [replyTarget, setReplyTarget] = useState<{
    threadId: string;
    parent: EntityComment;
  } | null>(null);

  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Lösch-Bestätigung über ConfirmDialog statt window.confirm
  const [confirmDelete, setConfirmDelete] = useState<{
    comment: EntityComment;
    threadId?: string;
  } | null>(null);

  const loadComments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setExtraReplies({});
    setReplyTarget(null);
    const result = await commentService.getComments(entityType, entityId, {
      limit: PAGE_SIZE,
      offset: 0,
    });
    setComments(result.comments);
    setTotal(result.total);
    setTotalAll(result.totalAll);
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const loadMore = async () => {
    if (loadingMore || !entityId) return;
    setLoadingMore(true);
    const result = await commentService.getComments(entityType, entityId, {
      limit: PAGE_SIZE,
      offset: comments.length,
    });
    setComments((prev) => {
      const known = new Set(prev.map((c) => c.id));
      return [...prev, ...result.comments.filter((c) => !known.has(c.id))];
    });
    setTotal(result.total);
    setTotalAll(result.totalAll);
    setLoadingMore(false);
  };

  // ------------------------------------------
  // Schreiben
  // ------------------------------------------

  const submitTopLevel = async (content: string) => {
    const created = await commentService.addComment(entityType, entityId, content);
    if (created) {
      // Optimistisch vorne einfügen
      setComments((prev) => [created, ...prev]);
      setTotal((t) => t + 1);
      setTotalAll((t) => t + 1);
    } else {
      await loadComments();
    }
  };

  const submitReply = async (threadId: string, parent: EntityComment, content: string) => {
    const created = await commentService.addComment(
      entityType,
      entityId,
      content,
      parent.id
    );
    if (created) {
      // Optimistisch in den Thread einfügen
      setExtraReplies((prev) =>
        prev[threadId] ? { ...prev, [threadId]: [...prev[threadId], created] } : prev
      );
      setComments((prev) =>
        prev.map((c) =>
          c.id === threadId
            ? {
                ...c,
                replyCount: (c.replyCount ?? 0) + 1,
                replies: [...(c.replies ?? []), created],
              }
            : c
        )
      );
      setTotalAll((t) => t + 1);
    } else {
      await loadComments();
    }
    setReplyTarget(null);
  };

  // ------------------------------------------
  // Löschen
  // ------------------------------------------

  const handleDelete = (comment: EntityComment, threadId?: string) => {
    if (!comment.id) return;
    setConfirmDelete({ comment, threadId });
  };

  const performDelete = async (comment: EntityComment, threadId?: string) => {
    setDeletingCommentId(comment.id);
    setDeleteError(null);
    try {
      await commentService.deleteComment(comment.id);
      if (threadId) {
        // Antwort lokal entfernen
        setExtraReplies((prev) =>
          prev[threadId]
            ? { ...prev, [threadId]: prev[threadId].filter((r) => r.id !== comment.id) }
            : prev
        );
        setComments((prev) =>
          prev.map((c) =>
            c.id === threadId
              ? {
                  ...c,
                  replyCount: Math.max(0, (c.replyCount ?? 0) - 1),
                  replies: (c.replies ?? []).filter((r) => r.id !== comment.id),
                }
              : c
          )
        );
        setTotalAll((t) => Math.max(0, t - 1));
      } else if ((comment.replyCount ?? 0) > 0) {
        // Top-Level mit Antworten: als gelöscht markieren (Antworten bleiben sichtbar)
        setComments((prev) =>
          prev.map((c) => (c.id === comment.id ? { ...c, isDeleted: true } : c))
        );
        setTotalAll((t) => Math.max(0, t - 1));
      } else {
        setComments((prev) => prev.filter((c) => c.id !== comment.id));
        setTotal((t) => Math.max(0, t - 1));
        setTotalAll((t) => Math.max(0, t - 1));
      }
    } catch (err: any) {
      setDeleteError(
        commentService.extractErrorMessage(err, tr('web.component.comment-section.kommentar-konnte-nicht-geloescht-werden'))
      );
    } finally {
      setDeletingCommentId(null);
    }
  };

  // ------------------------------------------
  // Antworten nachladen
  // ------------------------------------------

  const loadMoreReplies = async (comment: EntityComment) => {
    if (!comment.id || loadingRepliesFor) return;
    setLoadingRepliesFor(comment.id);
    const result = await commentService.getReplies(comment.id);
    setExtraReplies((prev) => ({ ...prev, [comment.id]: result.replies }));
    setLoadingRepliesFor(null);
  };

  const canDeleteComment = (comment: EntityComment): boolean =>
    !!user?.id && !!comment.authorId && comment.authorId === user.id;

  const hasMore = comments.length < total;

  return (
    <section aria-label={title}>
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5" style={{ color: ACCENT }} />
        <h2 className="text-lg font-semibold text-white">
          {title} {totalAll > 0 && `(${totalAll})`}
        </h2>
      </div>

      {/* Composer bzw. Login-Hinweis */}
      {isAuthenticated ? (
        <div className="mb-6">
          <CommentComposer onSubmit={submitTopLevel} />
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 mb-6 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">
            <LogIn size={16} style={{ color: ACCENT }} />
          </div>
          <p className="text-sm text-zinc-400">
            <RouterLink
              to="/login"
              className="text-[#3fced1] hover:text-[#35b5b8] font-medium"
            >
              {tr('web.component.comment-section.melde-dich-an')}
            </RouterLink>
            , um mitzudiskutieren.
          </p>
        </div>
      )}

      {deleteError && <p className="mb-4 text-xs text-red-400">{deleteError}</p>}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner color="primary" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-sm">
          {tr('web.component.comment-section.noch-keine-kommentare-sei-der')}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {comments.map((comment) => {
            const replies = extraReplies[comment.id] ?? comment.replies ?? [];
            const hasMoreReplies =
              !extraReplies[comment.id] && (comment.replyCount ?? 0) > replies.length;
            const isReplyingHere = replyTarget?.threadId === comment.id;

            return (
              <div key={comment.id}>
                <CommentItem
                  comment={comment}
                  canDelete={canDeleteComment(comment)}
                  isDeleting={deletingCommentId === comment.id}
                  onDelete={() => handleDelete(comment)}
                  onReply={
                    isAuthenticated && comment.isDeleted !== true
                      ? () => setReplyTarget({ threadId: comment.id, parent: comment })
                      : undefined
                  }
                />

                {(replies.length > 0 || hasMoreReplies || isReplyingHere) && (
                  <div className="ml-6 sm:ml-10 mt-2 flex flex-col gap-2 border-l border-zinc-800 pl-4">
                    {replies.map((reply) => (
                      // Verschachtelte Antworten pro Ebene weiter einrücken (Tiefe unbegrenzt,
                      // sichtbare Einrückung gedeckelt, siehe replyIndentPx)
                      <div
                        key={reply.id}
                        className={replyIndentPx(reply.depth) > 0 ? 'border-l border-zinc-800 pl-3' : ''}
                        style={replyIndentPx(reply.depth) > 0 ? { marginLeft: replyIndentPx(reply.depth) } : undefined}
                      >
                        <CommentItem
                          comment={reply}
                          isReply
                          canDelete={canDeleteComment(reply)}
                          isDeleting={deletingCommentId === reply.id}
                          onDelete={() => handleDelete(reply, comment.id)}
                          onReply={
                            isAuthenticated && reply.isDeleted !== true
                              ? () =>
                                  setReplyTarget({ threadId: comment.id, parent: reply })
                              : undefined
                          }
                        />
                      </div>
                    ))}

                    {hasMoreReplies && (
                      <button
                        type="button"
                        onClick={() => loadMoreReplies(comment)}
                        disabled={loadingRepliesFor === comment.id}
                        className="self-start inline-flex items-center gap-1 text-xs text-[#3fced1] hover:text-[#35b5b8] py-1 disabled:opacity-60"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                        {loadingRepliesFor === comment.id
                          ? tr('web.component.comment-section.antworten-werden-geladen')
                          : tr('web.component.comment-section.weitere-antworten-anzeigen', { replyCount: comment.replyCount })}
                      </button>
                    )}

                    {isReplyingHere && replyTarget && (
                      <div className="mt-1">
                        <p className="mb-1.5 text-xs text-zinc-500">
                          {tr('web.component.comment-section.antwort-an')}{' '}
                          <span className="font-semibold text-zinc-300">
                            {replyTarget.parent.authorName}
                          </span>
                        </p>
                        <CommentComposer
                          compact
                          autoFocus
                          placeholder={tr('web.component.comment-section.schreibe-deine-antwort')}
                          submitLabel={tr('web.component.comment-item.antworten')}
                          onCancel={() => setReplyTarget(null)}
                          onSubmit={(content) =>
                            submitReply(comment.id, replyTarget.parent, content)
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mehr laden */}
      {!loading && hasMore && (
        <div className="mt-5 flex justify-center">
          <Button
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            isLoading={loadingMore}
            onPress={loadMore}
            startContent={!loadingMore ? <ChevronDown size={16} /> : undefined}
          >
            {tr('web.component.comment-section.mehr-laden')}
          </Button>
        </div>
      )}

      {/* Bestätigung: Kommentar löschen */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title={tr('web.component.comment-section.kommentar-loeschen')}
        message={tr('web.component.comment-section.kommentar-wirklich-loeschen')}
        confirmLabel={tr('web.component.comment-item.loeschen')}
        danger
        onConfirm={() => {
          if (confirmDelete) {
            performDelete(confirmDelete.comment, confirmDelete.threadId);
          }
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </section>
  );
};

export default CommentSection;
